<template>
  <div class="overflow-hidden full-width full-height">
    <div class="d-flex align-center">
      <div class="flex-grow-0 flex-shrink-0" style="width: 40px">
        <div v-if="loading" class="mx-2">
          <i class="mdi mdi-spin mdi-loading"></i>
        </div>
        <div v-else-if="item.style == 'legend'" class="mx-2">
          <v-icon class="mdi mdi-map-legend" size="24"></v-icon>
        </div>
        <div v-else-if="typeof item.style == 'string'" class="mx-2">
          <v-icon size="24">
            {{ item.style }}
          </v-icon>
        </div>
        <LegendMapboxFilter
          :layer="item.style"
          :item="item"
          v-else-if="item.style"
        />
      </div>
      <span
        class="text-caption d-inline-block text-truncate text-left flex-grow-1"
        :title="item.name"
        @click="$emit('click', item)"
      >
        <span>{{ item.name }}</span>
      </span>
      <div class="d-flex flex-grow-0 align-center">
        <!-- Các nút custom khác -->
        <base-button
          @click="onAction(menu)"
          v-for="(menu, i) in item.extra_buttons"
          :key="'extra-'+i"
          class="mr-2"
          icon
          small
          :disabled="loading"
          :tooltip="menu.tooltip"
          v-bind="menu.attr"
        >
          <v-icon small> {{ menu.icon }} </v-icon>
        </base-button>

        <!-- 1. Nút Nhãn -->
        <base-button
          v-if="menus && menus.find(m => m.text && m.text.toLowerCase().includes('nhãn'))"
          class="mr-2" icon small :disabled="loading"
          :tooltip="menus.find(m => m.text && m.text.toLowerCase().includes('nhãn')).text"
          @click.stop="onAction(menus.find(m => m.text && m.text.toLowerCase().includes('nhãn')))"
        >
          <v-icon small>mdi-label-outline</v-icon>
        </base-button>

        <!-- 2. Zoom đến lớp (Goto) -->
        <base-button
          class="mr-2"
          icon
          small
          :disabled="loading"
          tooltip="Đi tới lớp"
          @click.stop="onToBounds"
          v-if="item.metadata.bounds"
        >
          <v-icon small>mdi-crosshairs-gps</v-icon>
        </base-button>

        <!-- 3. Bảng danh sách đối tượng -->
        <base-button
          v-if="menus && menus.find(m => m.text && m.text.toLowerCase().includes('danh sách đối tượng'))"
          class="mr-2" icon small :disabled="loading"
          :tooltip="menus.find(m => m.text && m.text.toLowerCase().includes('danh sách đối tượng')).text"
          @click.stop="onAction(menus.find(m => m.text && m.text.toLowerCase().includes('danh sách đối tượng')))"
        >
          <v-icon small>mdi-table</v-icon>
        </base-button>

        <!-- Các menu còn lại (nếu có) -->
        <base-button
          v-for="(menu, i) in (menus || []).filter(m => !m.text || (!m.text.toLowerCase().includes('nhãn') && !m.text.toLowerCase().includes('danh sách đối tượng')))"
          :key="'menu-'+i"
          class="mr-2"
          icon
          small
          :disabled="loading"
          :tooltip="menu.text"
          @click.stop="onAction(menu)"
        >
          <v-icon small v-if="menu.icon">{{ menu.icon }}</v-icon>
          <v-icon small v-else-if="menu.text && menu.text.toLowerCase().includes('kiểu hiển thị')">mdi-palette-outline</v-icon>
          <v-icon small v-else>mdi-tune</v-icon>
        </base-button>

        <!-- Xóa lớp (chỉ hiện khi không readonly) -->
        <base-button
          class="mr-2"
          icon
          small
          v-if="!readonly && !item.disable_delete"
          tooltip="Xóa lớp"
          @click.stop="onRemove"
        >
          <v-icon small>mdi-trash-can-outline</v-icon>
        </base-button>
        <slot name="extra-btn" />

        <!-- Checkbox hiển thị/ẩn lớp (Nằm ngoài cùng bên phải) -->
        <base-button
          icon
          small
          :disabled="loading"
          :tooltip="isShow ? 'Ẩn lớp bản đồ' : 'Hiển thị lớp bản đồ'"
          @click.stop="toggleShow"
        >
          <v-icon v-if="isShow" small color="primary">mdi-checkbox-marked</v-icon>
          <v-icon v-else small color="grey">mdi-checkbox-blank-outline</v-icon>
        </base-button>
      </div>
    </div>
    <div class="d-flex align-center">
      <div class="flex-grow-1">
        <v-slider
          v-if="!item.disabled_opacity"
          v-model="opacity"
          dense
          height="20px"
          hide-details
          max="1"
          min="0"
          :disabled="loading"
          small
          :color="item.color"
          step="0.1"
          thumb-label
          :thumb-size="24"
        ></v-slider>
      </div>
      <div class="text-right flex-grow-0 flex-shrink-0" style="width: 40px">
        <div v-if="item.legend">
          <v-btn @click="onLegendShow" small icon>
            <v-icon small>
              {{ isLegendShow ? 'mdi-menu-up' : 'mdi-menu-down' }}
            </v-icon>
          </v-btn>
        </div>
      </div>
    </div>
    <div
      v-if="item.legend"
      v-show="isLegendShow"
      style="max-height: 100px"
      class="overflow-auto"
    >
      <LayerLegend :value="item.legend" :layer="item" />
    </div>
  </div>
</template>

<script>
import { get, set } from 'lodash'

import LayerLegend from './layer-legend.vue'
import LegendMapboxFilter from './LegendMapboxFilter.vue'

export default {
  components: { LayerLegend, LegendMapboxFilter },
  props: {
    item: {
      type: Object,
      required: true,
      default: () => ({
        metadata: { loading: false, bounds: null },
        name: '',
        opacity: 1,
        show: true,
        legend_show: false,
        menus: [],
        disabled_opacity: false,
        disable_delete: false,
        legend: null,
        style: null,
        extra_buttons: [],
      }),
    },
    isCompare: Boolean,
    isSelected: Boolean,
    readonly: Boolean,
  },
  computed: {
    loading() {
      return this.item.metadata && this.item.metadata.loading
    },
    isShow() {
      return this.item.show
    },
    isLegendShow() {
      return this.item.legend_show
    },
    keyOpactiy() {
      return 'opacity'
    },
    opacity: {
      get() {
        return get(this.item, this.keyOpactiy, 1)
      },
      set(value) {
        let item = this.item
        set(item, this.keyOpactiy, value)
        this.$emit('update:item', item)
      },
    },
    menus() {
      return this.item.menus
    },
  },
  watch: {},
  methods: {
    toggleShow() {
      let item = this.item
      item.show = !item.show
      this.$emit('update:item', item)
    },
    onLegendShow() {
      let item = this.item
      item.legend_show = !item.legend_show
    },
    onToBounds() {
      this.$emit('click:bounds', this.item)
    },
    onRemove() {
      this.$emit('click:remove', this.item)
    },
    onAction(menu) {
      this.$emit('click:action', { menu, item: this.item })
    },
  },
}
</script>

<style scoped lang="scss">
.layer-item {
  position: relative;
  .draggable-handle {
    cursor: all-scroll;
    width: 20px;
    height: 100%;
  }
}
.layer-active {
  outline: 1px solid var(--v-primary-base) !important;
}
</style>
<style scoped>
:deep(.v-slider.v-slider--horizontal) {
  min-height: 20px;
}
</style>
